import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import clsx from 'clsx';
import {
  ArrowLeft,
  ArrowRight,
  BookOpenText,
  ChevronRight,
  Eye,
  FileText,
  PenLine,
  Plus,
  Search,
} from 'lucide-react';
import {
  SEARCH_MARK_END,
  SEARCH_MARK_START,
  denialFor,
  isAllowed,
  type GuideNavSection,
  type GuideSearchResult,
  type GuidesIndexResponse,
} from '@lp/shared';
import {
  Alert,
  Badge,
  Disclosure,
  EmptyState,
  LockedAction,
  Skeleton,
} from '../components/ui.js';
import { GuideMarkdown, type OutlineItem } from '../components/guides/GuideMarkdown.js';
import { ApiErrorView } from '../components/learner/StateViews.js';
import { useGuide, useGuideSearch, useGuidesIndex } from '../lib/useGuides.js';
import { useScrollSpy } from '../lib/useScrollSpy.js';
import { formatDate, pluralize } from '../lib/format.js';

/**
 * Guides — the reader side of the knowledge base.
 *
 * A documentation layout: a slate navigation sidebar, the article at a
 * readable measure, and an "On this page" outline when there is room. Every
 * active user reads every published guide, so nothing here checks enrollment;
 * the server has already filtered drafts out for anyone who may not see them.
 */

// --------------------------------------------------------------- the layout ---

function DocsLayout({
  index,
  activeSection,
  activeGuide,
  outline,
  children,
}: {
  index: GuidesIndexResponse | undefined;
  activeSection?: string;
  activeGuide?: string;
  outline?: ReactNode;
  children: ReactNode;
}) {
  const nav = <GuidesNav index={index} activeSection={activeSection} activeGuide={activeGuide} />;
  return (
    <div
      className={clsx(
        'mx-auto grid max-w-[92rem] grid-cols-1',
        'lg:grid-cols-[17rem_minmax(0,1fr)]',
        outline ? 'xl:grid-cols-[17rem_minmax(0,1fr)_15rem]' : '',
      )}
    >
      {/* From lg the navigation is a slate column that stays put; below it, it
          folds into a disclosure above the article. */}
      <aside
        aria-label="Guides navigation"
        className="hidden lg:block lg:sticky lg:top-14 lg:h-[calc(100dvh-3.5rem)] lg:overflow-y-auto bg-chrome text-chrome-ink border-r border-chrome-line"
      >
        {nav}
      </aside>

      <div className="min-w-0 px-4 sm:px-6 lg:px-10 py-7 lg:py-10">
        <div className="lg:hidden mb-6">
          <Disclosure summary={<span className="inline-flex items-center gap-2"><BookOpenText size={15} aria-hidden /> Browse all guides</span>}>
            <div className="-mx-4 -mb-4 mt-2 rounded-b-control bg-chrome text-chrome-ink">{nav}</div>
          </Disclosure>
        </div>
        {children}
      </div>

      {outline ? (
        <aside aria-label="On this page" className="hidden xl:block xl:sticky xl:top-14 xl:self-start xl:max-h-[calc(100dvh-3.5rem)] xl:overflow-y-auto py-10 pr-6">
          {outline}
        </aside>
      ) : null}
    </div>
  );
}

function GuidesNav({
  index,
  activeSection,
  activeGuide,
}: {
  index: GuidesIndexResponse | undefined;
  activeSection?: string | undefined;
  activeGuide?: string | undefined;
}) {
  return (
    <nav className="flex flex-col gap-6 px-4 py-6" aria-label="Guides">
      <SearchBox onChrome />

      <Link
        to="/guides"
        className={clsx(
          'flex items-center gap-2 rounded-control px-2.5 py-1.5 text-sm transition-colors duration-150 ease-out',
          !activeSection
            ? 'bg-chrome-2 font-semibold text-chrome-ink'
            : 'text-chrome-muted hover:bg-chrome-2 hover:text-chrome-ink',
        )}
      >
        <BookOpenText size={15} aria-hidden />
        All guides
      </Link>

      {!index
        ? Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="flex flex-col gap-2 px-2.5" aria-hidden>
              <div className="h-3 w-24 rounded-control bg-chrome-2" />
              <div className="h-3 w-40 rounded-control bg-chrome-2" />
              <div className="h-3 w-32 rounded-control bg-chrome-2" />
            </div>
          ))
        : index.sections.map((section) => (
            <div key={section.id}>
              <Link
                to={`/guides/${section.slug}`}
                className={clsx(
                  'block px-2.5 font-mono text-[0.6875rem] uppercase tracking-[0.08em] transition-colors duration-150 ease-out',
                  activeSection === section.slug && !activeGuide
                    ? 'text-chrome-accent-text'
                    : 'text-chrome-muted hover:text-chrome-ink',
                )}
              >
                {section.title}
              </Link>
              <ul className="mt-2 flex flex-col gap-0.5">
                {section.guides.length === 0 ? (
                  <li className="px-2.5 py-1 text-xs text-chrome-muted">Nothing here yet.</li>
                ) : (
                  section.guides.map((g) => {
                    const active = activeSection === section.slug && activeGuide === g.slug;
                    return (
                      <li key={g.id}>
                        <Link
                          to={`/guides/${section.slug}/${g.slug}`}
                          aria-current={active ? 'page' : undefined}
                          className={clsx(
                            'flex items-baseline gap-2 border-l-2 rounded-r-control py-1.5 pl-2.5 pr-2 text-sm leading-snug',
                            'transition-colors duration-150 ease-out',
                            active
                              ? 'border-chrome-accent bg-chrome-2 font-semibold text-chrome-ink'
                              : 'border-transparent text-chrome-muted hover:bg-chrome-2 hover:text-chrome-ink',
                          )}
                        >
                          <span className="min-w-0 flex-1">{g.title}</span>
                          {g.status === 'draft' ? (
                            <span className="shrink-0 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-chrome-accent-text">
                              Draft
                            </span>
                          ) : null}
                        </Link>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          ))}
    </nav>
  );
}

// ------------------------------------------------------------------ search ---

function SearchBox({ onChrome = false, autoFocus = false }: { onChrome?: boolean; autoFocus?: boolean }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [value, setValue] = useState(params.get('q') ?? '');
  useEffect(() => setValue(params.get('q') ?? ''), [params]);

  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        const q = value.trim();
        navigate(q ? `/guides?q=${encodeURIComponent(q)}` : '/guides');
      }}
    >
      <label className="relative block">
        <span className="sr-only">Search the guides</span>
        <Search
          size={15}
          aria-hidden
          className={clsx('pointer-events-none absolute left-3 top-1/2 -translate-y-1/2', onChrome ? 'text-chrome-muted' : 'text-ink-subtle')}
        />
        <input
          type="search"
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => {
            setValue(e.target.value);
            // On the guides home the results follow the typing; elsewhere the
            // search waits for Enter so the article in view stays put.
            if (!onChrome) {
              const q = e.target.value.trim();
              navigate(q ? `/guides?q=${encodeURIComponent(q)}` : '/guides', { replace: true });
            }
          }}
          placeholder="Search the guides"
          className={clsx(
            'w-full rounded-control border py-2 pl-9 pr-3 text-sm transition-colors duration-150 ease-out',
            onChrome
              ? 'border-chrome-line bg-chrome-2 text-chrome-ink placeholder:text-chrome-muted hover:border-chrome-muted'
              : 'border-control-line bg-surface text-ink placeholder:text-ink-subtle hover:border-ink-subtle py-3 text-base',
          )}
        />
      </label>
    </form>
  );
}

/** Render a snippet's delimited matches as <mark> — the API never sends HTML. */
function Snippet({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest.length) {
    const start = rest.indexOf(SEARCH_MARK_START);
    if (start < 0) {
      parts.push(rest);
      break;
    }
    const end = rest.indexOf(SEARCH_MARK_END, start);
    if (end < 0) {
      parts.push(rest.replaceAll(SEARCH_MARK_START, ''));
      break;
    }
    parts.push(rest.slice(0, start));
    parts.push(<mark key={key++}>{rest.slice(start + 1, end)}</mark>);
    rest = rest.slice(end + 1);
  }
  return <>{parts}</>;
}

/** The value, once it has stopped changing for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return settled;
}

function SearchResults({ q }: { q: string }) {
  // One request when the typing pauses, not one per keystroke.
  const settled = useDebounced(q, 250);
  const { data, isPending, isFetching, error, refetch } = useGuideSearch(settled);
  if (q.trim().length < 2) {
    return <p className="text-sm text-ink-muted">Keep typing — searches start at two characters.</p>;
  }
  if (error) return <ApiErrorView error={error} retry={() => void refetch()} context="the guides search" />;
  if (isPending) return <ResultsSkeleton />;

  const results: GuideSearchResult[] = data?.results ?? [];
  return (
    <section aria-labelledby="results-title" aria-busy={isFetching}>
      <h2 id="results-title" className="sr-only">
        Search results
      </h2>
      <p className="eyebrow mb-4" aria-live="polite">
        {results.length === 0
          ? `Nothing matches “${q.trim()}”`
          : `${pluralize(results.length, 'guide')} match “${q.trim()}”`}
      </p>
      {results.length === 0 ? (
        <p className="text-sm text-ink-muted max-w-prose leading-relaxed">
          Only published guides are searched. Try a shorter word, or browse the sections below.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface">
          {results.map((r) => (
            <li key={r.id}>
              <Link
                to={`/guides/${r.sectionSlug}/${r.slug}`}
                className="group block px-5 py-4 transition-colors duration-150 ease-out hover:bg-surface-2"
              >
                <p className="eyebrow">{r.sectionTitle}</p>
                <p className="mt-1 font-semibold text-ink group-hover:text-brand-text">{r.title}</p>
                <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
                  <Snippet text={r.snippet || r.summary} />
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ResultsSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden>
      {Array.from({ length: 3 }, (_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  );
}

// -------------------------------------------------------------------- home ---

export function GuidesHome() {
  const { data, error, refetch } = useGuidesIndex();
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';

  return (
    <DocsLayout index={data}>
      <div className="max-w-[72ch]">
        <p className="eyebrow">Guides</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-ink wrap-anywhere">
          How we do things here
        </h1>
        <p className="mt-3 text-base text-ink-muted leading-relaxed">
          How-tos and reference, written by guide writers and admins and open to everyone in the
          directory — whether or not you are enrolled in a course.
        </p>
        <div className="mt-6">
          <SearchBox autoFocus={!!q} />
        </div>
      </div>

      <div className="mt-10">
        {error ? (
          <ApiErrorView error={error} retry={() => void refetch()} context="the guides" />
        ) : q ? (
          <SearchResults q={q} />
        ) : !data ? (
          <div className="grid gap-4 md:grid-cols-2" aria-hidden>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        ) : data.sections.length === 0 ? (
          <EmptyState
            icon={<BookOpenText size={28} aria-hidden />}
            title="No guides yet"
            description="When a guide writer or an admin publishes a guide, it appears here for everyone."
            action={<WriteAGuide index={data} />}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold tracking-tight text-ink">Browse by section</h2>
              <WriteAGuide index={data} />
            </div>
            <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {data.sections.map((section) => (
                <SectionCard key={section.id} section={section} />
              ))}
            </ul>
          </>
        )}
      </div>
    </DocsLayout>
  );
}

function SectionCard({ section }: { section: GuideNavSection }) {
  const shown = section.guides.slice(0, 4);
  return (
    <li className="flex flex-col rounded-card border border-line bg-surface">
      <div className="border-b border-line px-5 py-4">
        <Link to={`/guides/${section.slug}`} className="group inline-flex items-center gap-1.5">
          <h3 className="font-semibold text-ink group-hover:text-brand-text">{section.title}</h3>
          <ChevronRight size={15} aria-hidden className="text-ink-subtle" />
        </Link>
        {section.description ? (
          <p className="mt-1 text-sm text-ink-muted">{section.description}</p>
        ) : null}
      </div>
      {shown.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-subtle">Nothing published here yet.</p>
      ) : (
        <ul className="flex flex-col py-2">
          {shown.map((g) => (
            <li key={g.id}>
              <Link
                to={`/guides/${section.slug}/${g.slug}`}
                className="flex items-start gap-2.5 px-5 py-2 transition-colors duration-150 ease-out hover:bg-surface-2"
              >
                <FileText size={15} aria-hidden className="mt-0.5 shrink-0 text-project" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink">
                    {g.title}
                    {g.status === 'draft' ? (
                      <Badge tone="warn" className="ml-2 align-middle">
                        Draft
                      </Badge>
                    ) : null}
                  </span>
                  {g.summary ? (
                    <span className="mt-0.5 block text-xs text-ink-muted line-clamp-2">{g.summary}</span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {section.guides.length > shown.length ? (
        <Link
          to={`/guides/${section.slug}`}
          className="mt-auto border-t border-line px-5 py-3 text-sm font-medium text-brand-text hover:underline"
        >
          All {section.guides.length} in {section.title}
        </Link>
      ) : null}
    </li>
  );
}

/** "Write a guide", or the same control locked with the server's reason. */
function WriteAGuide({ index }: { index: GuidesIndexResponse }) {
  if (index.create.allowed) {
    return (
      <Link
        to="/admin/guides?new=1"
        className="inline-flex items-center gap-2 rounded-control border border-control-line bg-surface px-3.5 py-2 text-sm font-medium text-ink transition-colors duration-150 ease-out hover:bg-surface-2"
      >
        <Plus size={15} aria-hidden />
        Write a guide
      </Link>
    );
  }
  return <LockedAction reason={index.create.reason ?? ''}>Write a guide</LockedAction>;
}

// ----------------------------------------------------------------- section ---

export function GuideSectionPage() {
  const { sectionSlug = '' } = useParams();
  const { data, error, refetch } = useGuidesIndex();
  const section = data?.sections.find((s) => s.slug === sectionSlug);

  return (
    <DocsLayout index={data} activeSection={sectionSlug}>
      {error ? (
        <ApiErrorView error={error} retry={() => void refetch()} context="the guides" />
      ) : !data ? (
        <Skeleton className="h-48 w-full max-w-[72ch]" />
      ) : !section ? (
        <EmptyState
          title="That section is not here"
          description="It may have been renamed or removed."
          action={<Link className="text-sm font-medium text-brand-text hover:underline" to="/guides">Back to all guides</Link>}
        />
      ) : (
        <div className="max-w-[72ch]">
          <Breadcrumbs items={[{ to: '/guides', label: 'Guides' }]} />
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-ink wrap-anywhere">{section.title}</h1>
          {section.description ? (
            <p className="mt-3 text-base text-ink-muted leading-relaxed">{section.description}</p>
          ) : null}
          {section.guides.length === 0 ? (
            <p className="mt-8 text-sm text-ink-muted">Nothing has been published in this section yet.</p>
          ) : (
            <ol className="mt-8 flex flex-col divide-y divide-line rounded-card border border-line bg-surface">
              {section.guides.map((g, i) => (
                <li key={g.id}>
                  <Link
                    to={`/guides/${section.slug}/${g.slug}`}
                    className="group flex items-start gap-4 px-5 py-4 transition-colors duration-150 ease-out hover:bg-surface-2"
                  >
                    <span className="mt-0.5 font-mono text-xs tabular-nums text-ink-subtle w-6 shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-ink group-hover:text-brand-text">
                        {g.title}
                        {g.status === 'draft' ? (
                          <Badge tone="warn" className="ml-2 align-middle">
                            Draft
                          </Badge>
                        ) : null}
                      </span>
                      {g.summary ? (
                        <span className="mt-1 block text-sm text-ink-muted leading-relaxed">{g.summary}</span>
                      ) : null}
                    </span>
                    <ChevronRight size={16} aria-hidden className="mt-1 shrink-0 text-ink-subtle" />
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </DocsLayout>
  );
}

function Breadcrumbs({ items }: { items: { to: string; label: string }[] }) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-1.5 text-sm text-ink-muted">
        {items.map((item, i) => (
          <li key={item.to} className="flex items-center gap-1.5">
            {i > 0 ? <ChevronRight size={13} aria-hidden className="text-ink-subtle" /> : null}
            <Link to={item.to} className="hover:text-ink hover:underline underline-offset-2">
              {item.label}
            </Link>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ------------------------------------------------------------------- guide ---

export function GuidePage() {
  const { sectionSlug = '', guideSlug = '' } = useParams();
  const index = useGuidesIndex();
  const { data, error, refetch } = useGuide(sectionSlug, guideSlug);
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const onOutline = useCallback((items: OutlineItem[]) => setOutline(items), []);
  const ids = useMemo(() => outline.map((o) => o.id), [outline]);
  const spied = useScrollSpy(ids);
  const atEnd = useAtPageEnd();
  // A short last section can never scroll its heading up into the reading
  // band, so once the page bottoms out the last heading is the one in view.
  const activeId = atEnd && ids.length ? ids[ids.length - 1]! : spied;

  // A guide with fewer than two headings has nothing worth outlining.
  const outlinePanel =
    outline.length >= 2 ? (
      <div>
        <p className="eyebrow mb-3">On this page</p>
        <ul className="flex flex-col gap-0.5 border-l border-line">
          {outline.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={clsx(
                  '-ml-px block border-l-2 py-1 text-sm leading-snug transition-colors duration-150 ease-out',
                  item.level === 3 ? 'pl-6' : 'pl-3',
                  activeId === item.id
                    ? 'border-brand text-ink font-medium'
                    : 'border-transparent text-ink-muted hover:text-ink',
                )}
              >
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    ) : undefined;

  return (
    <DocsLayout index={index.data} activeSection={sectionSlug} activeGuide={guideSlug} outline={outlinePanel}>
      {error ? (
        <div className="max-w-[72ch]">
          <ApiErrorView error={error} retry={() => void refetch()} context="this guide" />
          <p className="mt-4">
            <Link to="/guides" className="text-sm font-medium text-brand-text hover:underline">
              Back to all guides
            </Link>
          </p>
        </div>
      ) : !data ? (
        <div className="flex max-w-[72ch] flex-col gap-4" aria-hidden>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <article className="max-w-[72ch]">
          <Breadcrumbs
            items={[
              { to: '/guides', label: 'Guides' },
              { to: `/guides/${data.section.slug}`, label: data.section.title },
            ]}
          />

          {data.preview ? (
            <Alert tone="warn" title="Draft — only you and the admins can see this" className="mt-5" icon={<Eye size={16} />}>
              Readers will see this guide once it is published. It is not in search until then.
            </Alert>
          ) : data.guide.hasUnpublishedChanges && isAllowed(data.permissions, 'guide:update') ? (
            <Alert tone="info" title="You have unpublished changes" className="mt-5" icon={<PenLine size={16} />}>
              This is the version readers see. Your edits are waiting in the editor until you publish
              them.
            </Alert>
          ) : null}

          <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-ink wrap-anywhere">
            {data.guide.title}
          </h1>
          {data.guide.summary ? (
            <p className="mt-3 text-lg text-ink-muted leading-relaxed">{data.guide.summary}</p>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-line py-3">
            <p className="text-xs text-ink-muted">
              {data.preview
                ? `Draft by ${data.guide.author?.name ?? 'an author'}`
                : `Updated ${formatDate(data.guide.publishedAt)}${
                    data.guide.publishedBy ? ` by ${data.guide.publishedBy.name}` : ''
                  }`}
            </p>
            <EditThisGuide data={data} />
          </div>

          <div className="mt-8">
            <GuideMarkdown onOutline={onOutline} apps={data.apps}>
              {data.guide.bodyMd}
            </GuideMarkdown>
          </div>

          {data.previous || data.next ? (
            <nav aria-label="More in this section" className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {data.previous ? (
                <Link
                  to={`/guides/${data.previous.sectionSlug}/${data.previous.slug}`}
                  className="group rounded-card border border-line bg-surface px-5 py-4 transition-colors duration-150 ease-out hover:border-line-strong hover:bg-surface-2"
                >
                  <span className="eyebrow inline-flex items-center gap-1.5">
                    <ArrowLeft size={13} aria-hidden /> Previous
                  </span>
                  <span className="mt-1 block font-medium text-ink group-hover:text-brand-text">{data.previous.title}</span>
                </Link>
              ) : (
                <span className="hidden sm:block" />
              )}
              {data.next ? (
                <Link
                  to={`/guides/${data.next.sectionSlug}/${data.next.slug}`}
                  className="group rounded-card border border-line bg-surface px-5 py-4 text-right transition-colors duration-150 ease-out hover:border-line-strong hover:bg-surface-2"
                >
                  <span className="eyebrow inline-flex items-center gap-1.5">
                    Next <ArrowRight size={13} aria-hidden />
                  </span>
                  <span className="mt-1 block font-medium text-ink group-hover:text-brand-text">{data.next.title}</span>
                </Link>
              ) : null}
            </nav>
          ) : null}
        </article>
      )}
    </DocsLayout>
  );
}

/** True while the page is scrolled to (within a few pixels of) its end. */
function useAtPageEnd(): boolean {
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    const check = () => {
      const doc = document.documentElement;
      setAtEnd(doc.scrollHeight - (window.scrollY + window.innerHeight) < 4 && window.scrollY > 0);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);
  return atEnd;
}

function EditThisGuide({ data }: { data: NonNullable<ReturnType<typeof useGuide>['data']> }) {
  if (isAllowed(data.permissions, 'guide:update')) {
    return (
      <Link
        to={`/admin/guides/${data.guide.id}`}
        className="inline-flex items-center gap-1.5 rounded-control border border-control-line bg-surface px-3 py-1.5 text-sm font-medium text-ink transition-colors duration-150 ease-out hover:bg-surface-2"
      >
        <PenLine size={14} aria-hidden />
        Edit this guide
      </Link>
    );
  }
  const denial = denialFor(data.permissions, 'guide:update');
  // A reader who could never write a guide does not need a locked button in
  // their way; a guide writer looking at someone else's does, so they know whom to ask.
  if (!denial || denial.code === 'trainers_only') return null;
  return <LockedAction reason={denial.reason}>Edit this guide</LockedAction>;
}
